import React, { useState, useEffect, useRef } from 'react';
import './AdminNav.css';
import { useNavigate } from 'react-router-dom';

type AdminNavProps = {
    collapsed: boolean;
    setCollapsed: (value: boolean) => void;
    user: any;
};

const AdminNav: React.FC<AdminNavProps> = ({ collapsed, setCollapsed, user }) => {
    const navigate = useNavigate();
    const [isBusinessOpen, setIsBusinessOpen] = useState(false);
    const [isNewsOpen, setIsNewsOpen] = useState(false);
    const popupRef = useRef<HTMLUListElement | null>(null);
    const pagesButtonRef = useRef<HTMLDivElement | null>(null);
    const [popupTop, setPopupTop] = useState(0);

    useEffect(() => {
        if (collapsed) {
            setIsBusinessOpen(false);
            setIsNewsOpen(false);
        }
    }, [collapsed]);

    useEffect(() => {
        if (isBusinessOpen || isNewsOpen) setCollapsed(false);
    }, [isBusinessOpen, isNewsOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node) &&
                !pagesButtonRef.current?.contains(event.target as Node)
            ) {
                setIsBusinessOpen(false);
                setIsNewsOpen(false);
            }
        };

        if (collapsed && (isBusinessOpen || isNewsOpen)) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isBusinessOpen, isNewsOpen, collapsed]);

    const toggleBusiness = () => {
        if (collapsed && pagesButtonRef.current) {
            setPopupTop(pagesButtonRef.current.offsetTop);
        }
        setIsBusinessOpen(prev => !prev);
    };

    const toggleNews = () => {
        if (collapsed && pagesButtonRef.current) {
            setPopupTop(pagesButtonRef.current.offsetTop);
        }
        setIsNewsOpen(prev => !prev);
    };

    return (
        <nav className={`admin-nav ${collapsed ? 'collapsed' : 'expanded'}`}>
            <ul>
                {/* Home */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard', { state: { user } })}>
                        <svg className="nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875a1.125 1.125 0 0 1 1.125-1.125h2.25A1.125 1.125 0 0 1 14.25 15v4.875h4.125A1.125 1.125 0 0 0 19.5 18.75V9.75" />
                        </svg>
                        <span className="nav-text">Home</span>
                    </div>
                </li>

                {/* Profile */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/view-user', { state: { user } })}>
                        <svg className="nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                        <span className="nav-text">Profile</span>
                    </div>
                </li>

                {/* Home Images */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard/home/images', { state: { user } })}>
                        <svg className="nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H5.625A1.125 1.125 0 0 0 4.5 3.75v16.5A1.125 1.125 0 0 0 5.625 21h12.75A1.125 1.125 0 0 0 19.5 19.875V11.25a9 9 0 0 0-9-9" />
                        </svg>
                        <span className="nav-text">Home Images</span>
                    </div>
                </li>

                {/* Our Business */}
                <li>
                    <div className="nav-item-content" onClick={toggleBusiness} ref={pagesButtonRef}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="nav-icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                        </svg>
                        <span className="nav-text">Our Business</span>
                    </div>
                    {isBusinessOpen && (
                        <ul
                            ref={popupRef}
                            className={`submenu-nav ${collapsed ? 'submenu-nav-popup' : ''}`}
                            style={collapsed ? { top: `${popupTop}px`, left: '65px' } : {}}
                        >
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/business/our-story', { state: { user } })}>Our Story</li>
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/business/our-vision', { state: { user } })}>Our Vision</li>
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/business/our-approach', { state: { user } })}>Our Approach</li>
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/business/partnership', { state: { user } })}>Partnership</li>
                        </ul>
                    )}
                </li>

                {/* News */}
                <li>
                    <div className="nav-item-content" onClick={toggleNews} ref={pagesButtonRef}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="nav-icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46" />
                        </svg>
                        <span className="nav-text">News</span>
                    </div>
                    {isNewsOpen && (
                        <ul
                            ref={popupRef}
                            className={`submenu-nav ${collapsed ? 'submenu-nav-popup' : ''}`}
                            style={collapsed ? { top: `${popupTop}px`, left: '65px' } : {}}
                        >
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/news/list', { state: { user } })}>Stories</li>
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/insights/list', { state: { user } })}>Webinars</li>
                            <li className="submenu-nav-item" onClick={() => navigate('/dashboard/podcasts/list', { state: { user } })}>Podcasts</li>
                        </ul>
                    )}
                </li>

                {/* Vacancies */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard/vacancies/list', { state: { user } })}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="nav-icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 19.5v-.75a7.5 7.5 0 0 0-7.5-7.5H4.5m0-6.75h.75c7.87 0 14.25 6.38 14.25 14.25v.75M6 18.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                        </svg>
                        <span className="nav-text">Vacancies</span>
                    </div>
                </li>

                {/* Wines */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard/wines/list', { state: { user } })}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className='nav-icon'>
                            <path fill='white' stroke='white' d="M393.4 9.4c12.5-12.5 32.8-12.5 45.3 0l64 64c12.5 12.5 12.5 32.8 0 45.3c-11.8 11.8-30.7 12.5-43.2 1.9l-9.5 9.5-48.8 48.8c-9.2 9.2-11.5 22.9-8.6 35.6c9.4 40.9-1.9 85.6-33.8 117.5L197.3 493.3c-25 25-65.5 25-90.5 0l-88-88c-25-25-25-65.5 0-90.5L180.2 153.3c31.9-31.9 76.6-43.1 117.5-33.8c12.6 2.9 26.4 .5 35.5-8.6l48.8-48.8 9.5-9.5c-10.6-12.6-10-31.4 1.9-43.2zM99.3 347.3l65.4 65.4c6.2 6.2 16.4 6.2 22.6 0l97.4-97.4c6.2-6.2 6.2-16.4 0-22.6l-65.4-65.4c-6.2-6.2-16.4-6.2-22.6 0L99.3 324.7c-6.2 6.2-6.2 16.4 0 22.6z" />
                        </svg>
                        <span className="nav-text">Wines</span>
                    </div>
                </li>

                {/* Contact */}
                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard/offices/list', { state: { user } })}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="nav-icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
                        </svg>
                        <span className="nav-text">Contact</span>
                    </div>
                </li>


                <li>
                    <div className="nav-item-content" onClick={() => navigate('/dashboard/documents/list', { state: { user } })}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="nav-icon"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14 2v6h6"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 13h8"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 17h8"
                            />
                        </svg>

                        <span className="nav-text">Files</span>
                    </div>
                </li>
                <li>
                    <div
                        className="nav-item-content"
                        onClick={() => navigate('/dashboard/galleries/list', { state: { user } })}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="nav-icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18v14H3V5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 5l7 7 4-4 5 5" />
                        </svg>


                        <span className="nav-text">Galleries</span>
                    </div>
                </li>

            </ul>
        </nav>
    );
};

export default AdminNav;
